/**
 * v77.99.77: Pure helpers and small presentational sub-components used
 * by pages/Children.jsx. Extracted to keep the page file under the 1000-
 * line maintainability budget. None of these read from page-local
 * scope — they take everything they need via props/args.
 */
import React from "react";

/** Years since DOB. Returns null when DOB is missing or unparseable. */
export function ageFromDob(dob) {
  if (!dob) return null;
  const d = new Date(dob + (dob.length === 10 ? "T00:00:00" : ""));
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age;
}

/**
 * v77.79 — Age in years + months for the Children card. Returns a
 * sentence-case label, falling back gracefully for under-1-year or
 * exact-birthday cases. Returns null when DOB is invalid.
 */
export function ageDetailFromDob(dob) {
  if (!dob) return null;
  const d = new Date(dob + (dob.length === 10 ? "T00:00:00" : ""));
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let years = now.getFullYear() - d.getFullYear();
  let months = now.getMonth() - d.getMonth();
  if (now.getDate() < d.getDate()) months -= 1;
  if (months < 0) { years -= 1; months += 12; }
  if (years < 0) return null;
  // v77.99.61 — Sentence-case ("10 years, 3 months") replaces the
  // ALLCAPS database-field look ("10 YRS 3 MTHS"). Same data, much
  // warmer read.
  const yrLabel = years === 1 ? "year" : "years";
  const moLabel = months === 1 ? "month" : "months";
  if (years === 0) return `${months} ${moLabel}`;
  if (months === 0) return `${years} ${yrLabel}`;
  return `${years} ${yrLabel}, ${months} ${moLabel}`;
}

/**
 * v76.2 — Compact relative duration ("today" / "3d" / "2w" / "5mo" /
 * "1y") for the "Shared with X · {since}" pill on the Children list.
 */
export function relativeDuration(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const days = Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
  if (days === 0) return "today";
  if (days < 7) return `${days}d`;
  if (days < 30) return `${Math.floor(days / 7)}w`;
  if (days < 365) return `${Math.floor(days / 30)}mo`;
  return `${Math.floor(days / 365)}y`;
}

/** Format an ISO timestamp as "12 Mar 2026 14:32" — locale-independent. */
export function formatHistoryAt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const day = d.getDate();
  const mon = months[d.getMonth()];
  const yr = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${day} ${mon} ${yr} ${hh}:${mm}`;
}

export const SECTION_LABELS = {
  medical: "Medical",
  school: "School",
  emergency: "Emergency",
  general: "Identity",
};

/**
 * v76.2 — "What I'm sharing" line under each child card. Three states:
 *   1. Active co-parent linked → "Shared with {Name} · {since}" (teal)
 *   2. No co-parent yet, pending invite → "Pending invite sent · …" (amber)
 *   3. Nothing → "No co-parent assigned" + optional invite shortcut
 * Mirrors Settings → Co-parent access exactly so the two pages can
 * never contradict each other again.
 */
export function ChildSharingLine({ child, onAddCoparent }) {
  const sharing = child.sharing;
  if (!sharing) return null;
  if (child.archived_at) return null;
  const cps = sharing.coparents || [];
  const pending = sharing.pending_invites || [];
  if (cps.length === 0) {
    if (pending.length > 0) {
      const first = pending[0];
      const target = first?.name || first?.email;
      return (
        <div
          className="mt-2 text-[11px] text-[#8F6B1F] inline-flex items-center gap-1.5"
          data-testid={`child-sharing-pending-${child.id}`}
          title={target ? `Pending invite to ${target}` : "Pending invite sent — awaiting acceptance"}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[#E6A92F]" />
          Pending invite{pending.length > 1 ? `s (${pending.length})` : ""} · awaiting acceptance
        </div>
      );
    }
    return (
      <div
        className="mt-2 text-[11px] text-[#6F828A] inline-flex items-center gap-1.5 flex-wrap"
        data-testid={`child-sharing-empty-${child.id}`}
      >
        <span className="w-1.5 h-1.5 rounded-full bg-[#D9E1E3]" />
        <span>No co-parent assigned</span>
        {/* v77.22.5 — Inline quick-invite. Only rendered when truly
            empty (no active co-parent AND no pending invite) so we
            never nag users mid-flow. The detail view's full Invite
            CTA still exists; this is the at-a-glance shortcut. */}
        {onAddCoparent && (
          <button
            type="button"
            onClick={onAddCoparent}
            className="text-[#116B6E] hover:text-[#0F5658] underline underline-offset-2 decoration-dotted hover:decoration-solid font-medium"
            data-testid={`child-sharing-add-coparent-${child.id}`}
            aria-label={`Invite a co-parent for ${child.name}`}
          >
            · Invite a co-parent
          </button>
        )}
      </div>
    );
  }
  // Earliest membership wins as the "since" anchor — that's the date
  // the first co-parent gained access (most meaningful answer to "how
  // long has this been shared?").
  const earliest = cps
    .map((c) => c.since)
    .filter(Boolean)
    .sort()[0];
  const since = earliest ? relativeDuration(earliest) : null;
  const names = cps.slice(0, 2).map((c) => c.name || c.email).join(", ");
  const more = cps.length > 2 ? ` +${cps.length - 2}` : "";
  return (
    <div
      className="mt-2 text-[11px] text-[#116B6E] inline-flex items-center gap-1.5"
      data-testid={`child-sharing-${child.id}`}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-[#1F9FA4]" />
      <span>
        Shared with <span className="font-medium text-[#0F5658]">{names}{more}</span>
        {since ? <span className="text-[#6F828A]"> · {since}</span> : null}
      </span>
    </div>
  );
}

/**
 * Small inline badge: "Last updated by NAME · 12 Mar 14:32" for a given
 * section. Self-hides when the section has no recorded changes yet.
 */
export function SectionLastUpdated({ section, sectionsForChild }) {
  const info = sectionsForChild?.[section];
  if (!info?.at) return null;
  return (
    <span
      className="text-[10px] text-[#6F828A] font-normal normal-case tracking-normal ml-auto"
      data-testid={`section-last-updated-${section}`}
      title={`Last updated by ${info.by_name || "—"} on ${formatHistoryAt(info.at)}`}
    >
      Last updated by {info.by_name || "—"} · {formatHistoryAt(info.at)}
    </span>
  );
}
